class ProductGalleryThumbnails extends HTMLElement {
  constructor() {
    super();
    this.thumbnails = this.querySelectorAll('.product-gallery__thumbnail');
    this.mainMediaWrapper = this.closest('.product__media-wrapper');
    this.currentMediaId = null;

  }

  connectedCallback() {

    this.setupEventListeners();
    this.setupKeyboardNavigation();

    const activeThumb = this.querySelector('.product-gallery__thumbnail--active');
    if (activeThumb) {
      this.currentMediaId = activeThumb.dataset.mediaId;
    } else {
      console.warn('ProductGallery: No active thumbnail found on initialization');
    }

    this.initializeGallery();
  }

  /**
   * Set up event listeners for thumbnails and variant changes
   */
  setupEventListeners() {
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
    });

    // Update gallery when product variant changes
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event.data?.variant;
      if (variant?.featured_media) {
        this.setActiveMedia(variant.featured_media.id);
      }
    });
  }

  /**
   * Handle thumbnail click events
   * @param {Event} event - Click event
   */
  handleThumbnailClick(event) {
    event.preventDefault();
    const thumbnail = event.currentTarget;
    const mediaId = thumbnail.dataset.mediaId;
    const position = thumbnail.dataset.mediaPosition;


    if (mediaId === this.currentMediaId) {
      return;
    }

    this.setActiveMedia(mediaId);
    this.updateMainMedia(mediaId, position);
  }

  /**
   * Initialize the gallery state and preload videos
   */
  initializeGallery() {
    const mediaList = this.mainMediaWrapper.querySelector('.product__media-list');
    if (!mediaList) return;

    const allMediaItems = mediaList.querySelectorAll('.product-gallery__media-item');
    if (allMediaItems.length === 0) return;

    // Preload videos to prevent poster flash
    allMediaItems.forEach(item => {
      this.preloadVideos(item);
    });

    allMediaItems.forEach((item, index) => {
      if (index === 0) {
        item.classList.add('product-gallery__media-item--active');
      } else {
        item.classList.remove('product-gallery__media-item--active');
      }
    });
  }

  /**
   * Update the main product media display
   * @param {string} mediaId - Media ID to display
   * @param {string} position - Media position
   */
  updateMainMedia(mediaId, position) {
    const mediaList = this.getMediaList();
    if (!mediaList) return;

    const allMediaItems = this.getAllMediaItems(mediaList);
    if (!allMediaItems.length) return;

    this.deactivateCurrentMedia(allMediaItems);
    this.activateTargetMedia(mediaList, mediaId, allMediaItems);
    this.dispatchMediaChangeEvent(mediaId, position);
  }

  /**
   * Get the media list container
   * @returns {HTMLElement|null} Media list element
   */
  getMediaList() {
    const mediaList = this.mainMediaWrapper.querySelector('.product__media-list');
    if (!mediaList) {
      console.warn('ProductGallery: Media list not found');
    }
    return mediaList;
  }

  /**
   * Get all media items from the media list
   * @param {HTMLElement} mediaList - Media list container
   * @returns {NodeList} All media items
   */
  getAllMediaItems(mediaList) {
    const allMediaItems = mediaList.querySelectorAll('.product-gallery__media-item');
    if (allMediaItems.length === 0) {
      console.warn('ProductGallery: No media items found');
    }
    return allMediaItems;
  }

  /**
   * Deactivate current media and pause videos
   * @param {NodeList} allMediaItems - All media items
   */
  deactivateCurrentMedia(allMediaItems) {
    allMediaItems.forEach(item => {
      if (item.classList.contains('product-gallery__media-item--active')) {
        this.pauseVideosInItem(item);
      }
      item.classList.remove('product-gallery__media-item--active');
    });
  }

  /**
   * Activate target media or fallback to first item
   * @param {HTMLElement} mediaList - Media list container
   * @param {string} mediaId - Target media ID
   * @param {NodeList} allMediaItems - All media items
   * @returns {HTMLElement|null} Activated media element
   */
  activateTargetMedia(mediaList, mediaId, allMediaItems) {
    const targetMedia = mediaList.querySelector(`.product-gallery__media-item[data-media-id="${mediaId}"]`);
    
    if (targetMedia) {
      return this.activateMediaItem(targetMedia);
    } else {
      return this.activateFallbackMedia(allMediaItems, mediaId);
    }
  }

  /**
   * Activate a specific media item
   * @param {HTMLElement} mediaItem - Media item to activate
   * @returns {HTMLElement} The activated media item
   */
  activateMediaItem(mediaItem) {
    mediaItem.classList.add('product-gallery__media-item--active');
    this.triggerLazyLoad(mediaItem);
    this.resumeVideosInItem(mediaItem);
    return mediaItem;
  }

  /**
   * Activate fallback media (first item) when target not found
   * @param {NodeList} allMediaItems - All media items
   * @param {string} mediaId - Original target media ID
   * @returns {HTMLElement|null} Activated fallback media
   */
  activateFallbackMedia(allMediaItems, mediaId) {
    if (allMediaItems.length > 0) {
      const fallbackMedia = allMediaItems[0];
      this.activateMediaItem(fallbackMedia);
      console.warn(`ProductGallery: Target media ${mediaId} not found, showing first media`);
      return fallbackMedia;
    }
    return null;
  }

  /**
   * Pause all videos in a media item
   * @param {HTMLElement} mediaItem - Media item containing videos
   */
  pauseVideosInItem(mediaItem) {
    const videos = mediaItem.querySelectorAll('video');
    videos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  /**
   * Resume videos in a media item if they were previously playing
   * @param {HTMLElement} mediaItem - Media item containing videos
   */
  resumeVideosInItem(mediaItem) {
    const isVideo = mediaItem.querySelector('.product-media-container')?.classList.contains('media-type-video');
    if (!isVideo) return;

    const videos = mediaItem.querySelectorAll('video');
    videos.forEach(video => {
      if (video.paused && video.currentTime > 0) {
        video.play().catch(() => {
          // Silently handle autoplay restrictions
        });
      }
    });
  }

  /**
   * Dispatch media change event for other components
   * @param {string} mediaId - Media ID
   * @param {string} position - Media position
   */
  dispatchMediaChangeEvent(mediaId, position) {
    this.dispatchEvent(new CustomEvent('gallery:media-changed', {
      bubbles: true,
      detail: { mediaId, position }
    }));
  }

  /**
   * Trigger lazy loading for images and videos in a media container
   * @param {HTMLElement} mediaContainer - Media container element
   */
  triggerLazyLoad(mediaContainer) {
    // Force image refresh to trigger lazy loading
    const allImages = mediaContainer.querySelectorAll('img');
    allImages.forEach(img => {
      const originalSrc = img.src;
      if (originalSrc) {
        img.src = '';
        img.src = originalSrc;
      }
    });

    // Videos are already loaded and shouldn't be reset

    // Handle deferred media loading
    const deferredMedia = mediaContainer.querySelectorAll('deferred-media, product-model');
    deferredMedia.forEach(deferred => {
      if (!deferred.hasAttribute('loaded')) {
        if (typeof deferred.loadContent === 'function') {
          try {
            deferred.loadContent();
          } catch {
          }
        }

        // Fallback: trigger poster button
        const posterButton = deferred.querySelector('.deferred-media__poster');
        if (posterButton) {
          posterButton.click();
        }
      } else {
        const videos = deferred.querySelectorAll('video');
        videos.forEach(video => {
          if (video.paused) {
            video.play().catch(() => {
            });
          }
        });
      }
    });

    // Force layout recalculation to ensure proper rendering
    mediaContainer.offsetHeight;
  }

  /**
   * Set the active thumbnail and update UI state
   * @param {string} mediaId - Media ID to set as active
   */
  setActiveMedia(mediaId) {
    this.currentMediaId = mediaId;

    this.thumbnails.forEach(thumbnail => {
      const isActive = thumbnail.dataset.mediaId === mediaId;
      thumbnail.classList.toggle('product-gallery__thumbnail--active', isActive);
      thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    // Scroll to show active thumbnail
    const activeThumbnail = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (activeThumbnail) {
      this.scrollToThumbnail(activeThumbnail);
    }
  }

  /**
   * Scroll to ensure thumbnail is visible in the container
   * @param {HTMLElement} thumbnail - Thumbnail element to scroll to
   */
  scrollToThumbnail(thumbnail) {
    const wrapper = this.querySelector('.product-gallery__thumbnails-wrapper');
    if (!wrapper) return;

    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      const thumbnailLeft = thumbnail.offsetLeft;
      const thumbnailWidth = thumbnail.offsetWidth;
      const wrapperWidth = wrapper.offsetWidth;
      const scrollLeft = wrapper.scrollLeft;

      if (thumbnailLeft < scrollLeft) {
        wrapper.scrollLeft = thumbnailLeft;
      } else if (thumbnailLeft + thumbnailWidth > scrollLeft + wrapperWidth) {
        wrapper.scrollLeft = thumbnailLeft + thumbnailWidth - wrapperWidth;
      }
    } else {
      const thumbnailTop = thumbnail.offsetTop;
      const thumbnailHeight = thumbnail.offsetHeight;
      const wrapperHeight = wrapper.offsetHeight;
      const scrollTop = wrapper.scrollTop;

      if (thumbnailTop < scrollTop) {
        wrapper.scrollTop = thumbnailTop;
      } else if (thumbnailTop + thumbnailHeight > scrollTop + wrapperHeight) {
        wrapper.scrollTop = thumbnailTop + thumbnailHeight - wrapperHeight;
      }
    }
  }

  /**
   * Set up keyboard navigation for thumbnails
   */
  setupKeyboardNavigation() {
    this.addEventListener('keydown', (event) => {
      const currentIndex = Array.from(this.thumbnails).findIndex(
        thumb => thumb.dataset.mediaId === this.currentMediaId
      );

      let newIndex;
      const isMobile = window.innerWidth < 750;

      switch (event.key) {
        case 'ArrowLeft':
          if (isMobile) {
            newIndex = Math.max(0, currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (isMobile) {
            newIndex = Math.min(this.thumbnails.length - 1, currentIndex + 1);
          }
          break;
        case 'ArrowUp':
          if (!isMobile) {
            event.preventDefault();
            newIndex = Math.max(0, currentIndex - 1);
          }
          break;
        case 'ArrowDown':
          if (!isMobile) {
            event.preventDefault();
            newIndex = Math.min(this.thumbnails.length - 1, currentIndex + 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = this.thumbnails.length - 1;
          break;
        default:
          return;
      }

      if (newIndex !== undefined && newIndex !== currentIndex) {
        this.thumbnails[newIndex].click();
        this.thumbnails[newIndex].focus();
      }
    });
  }

  /**
   * Preload videos and hide posters to prevent flash
   * @param {HTMLElement} mediaItem - Media item containing videos
   */
  preloadVideos(mediaItem) {
    const deferredMedia = mediaItem.querySelectorAll('deferred-media');
    deferredMedia.forEach(deferred => {
      // Hide poster permanently for gallery
      const poster = deferred.querySelector('.deferred-media__poster');
      if (poster) {
        poster.style.display = 'none';
      }

      if (typeof deferred.loadContent === 'function') {
        deferred.loadContent();
      } else if (poster) {
        // Trigger poster to load video
        poster.click();
        poster.style.display = 'none';
      }
    });
  }


  /**
   * Update URL with media parameter for deep linking
   * @param {string} mediaId - Media ID to add to URL
   */
  updateURL(mediaId) {
    if (!mediaId || !window.history.replaceState) return;

    const url = new URL(window.location);
    url.searchParams.set('media', mediaId);
    window.history.replaceState({}, '', url);
  }
}

if (!customElements.get('product-gallery-thumbnails')) {
  customElements.define('product-gallery-thumbnails', ProductGalleryThumbnails);
}
